import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { useTranslation } from 'react-i18next';
import { useLocale, fmtNumber } from '../i18n/format';
import { resolveCountryFlag } from '../constants/countries';

function Section({ title, data, locale, tableLabels }) {
  if (!Array.isArray(data) || data.length === 0) return null;

  const totalCount = data.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  const segments = data.map((item, idx) => {
    const count = Number(item.count) || 0;
    const pct =
      typeof item.pct === 'number' && Number.isFinite(item.pct)
        ? Math.max(0, item.pct)
        : (totalCount > 0 ? (count / totalCount) * 100 : 0);
    return {
      key: `${item.name || 'Other'}-${idx}`,
      name: item.name || 'Other',
      count,
      pct,
      color: PIE_COLORS[idx % PIE_COLORS.length],
    };
  });

  let cursor = 0;
  const gradientParts = segments.map((seg) => {
    const from = cursor;
    const value = Math.max(0, Math.min(100, seg.pct));
    const to = Math.min(100, cursor + value);
    cursor = to;
    return `${seg.color} ${from}% ${to}%`;
  });
  if (cursor < 100) {
    gradientParts.push(`${PIE_CHART_BG} ${cursor}% 100%`);
  }
  const pieBackground = gradientParts.length
    ? `conic-gradient(${gradientParts.join(', ')})`
    : PIE_CHART_BG;

  const nameLabel = tableLabels?.name || 'Name';
  const countLabel = tableLabels?.count || 'Count';
  const percentLabel = tableLabels?.percent || '%';

  return (
    <div style={{ marginTop: 18 }}>
      <h3 style={{ margin: '6px 0 10px 0' }}>{title}</h3>
      <div style={sectionContentStyle}>
        <div style={sectionTableWrapperStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em' }}>
            <thead>
              <tr>
                <th style={thTd}>{nameLabel}</th>
                <th style={thTd}>{countLabel}</th>
                <th style={thTd}>{percentLabel}</th>
              </tr>
            </thead>
            <tbody>
              {segments.map((seg, idx) => (
                <tr key={seg.key || idx}>
                  <td style={thTd}>
                    <span style={{ ...legendColorDotStyle, background: seg.color, marginRight: 8 }} />
                    {seg.name}
                  </td>
                  <td style={thTd}>{fmtNumber(seg.count, locale)}</td>
                  <td style={thTd}>{seg.pct.toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={pieBlockStyle}>
          <div style={{ ...pieChartStyle, background: pieBackground }}>
            <span style={pieChartCenterStyle}>{fmtNumber(totalCount, locale)}</span>
          </div>
          <div style={pieLegendStyle}>
            {segments.map((seg, idx) => (
              <div key={`legend-${seg.key || idx}`} style={legendRowStyle}>
                <span style={{ ...legendColorDotStyle, background: seg.color }} />
                <span style={{ flex: 1 }}>{seg.name}</span>
                <span style={{ fontWeight: 600 }}>{seg.pct.toFixed(2)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UserAgentServicePage() {
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { isAuth } = useAuth();
  const { t } = useTranslation('common');
  const locale = useLocale();
  const tableLabels = React.useMemo(() => ({
    name: t('user_agent.table.name'),
    count: t('user_agent.table.count'),
    percent: t('user_agent.table.percent'),
  }), [t]);
  const sectionTitles = React.useMemo(() => ({
    deviceType: t('user_agent.sections.device_type'),
    os: t('user_agent.sections.os'),
    deviceFamily: t('user_agent.sections.device_family'),
    client: t('user_agent.sections.client'),
    browser: t('user_agent.sections.browser'),
  }), [t]);
  const totalLabel = t('user_agent.total_sessions');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [country, setCountry] = useState('');
  const [countries, setCountries] = useState([]);

  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!startDate) {
      const today = new Date();
      setStartDate(today.toISOString().slice(0, 10));
      setEndDate(today.toISOString().slice(0, 10));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!isAuth || !radioId || !startDate || !endDate) {
      if (!isAuth) setCountries([]);
      return;
    }
    const API = (API_BASE_URL || '').replace(/\/+$/, '');
    const url =
      `${API}/radios/${encodeURIComponent(radioId)}/metrics/geo/countries` +
      `?start_date=${startDate}&end_date=${endDate}`;

    authFetch(url)
      .then((r) => r.json())
      .then((json) => {
        if (json.data) {
          const names = json.data.map((r) => r.country).filter(Boolean);
          const unique = Array.from(new Set(names));
          const mapped = unique.map((name) => ({
            name,
            flag: resolveCountryFlag(name) || '',
          }));
          setCountries(mapped);
        }
      })
      .catch(() => setCountries([]));
  }, [isAuth, radioId, startDate, endDate]);

  const fetchSummary = useCallback(async () => {
    if (!isAuth) {
      setError('');
      setSummary(null);
      setTotal(null);
      return;
    }
    if (!radioId || !startDate || !endDate) {
      setError(t('listeners.errors.select_dates'));
      setSummary(null);
      setTotal(null);
      return;
    }

    setError('');
      setLoading(true);
    try {
      let common =
        `start_date=${startDate}&end_date=${endDate}`;
      if (country) common += `&country=${encodeURIComponent(country)}`;

      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const base = `${API}/radios/${encodeURIComponent(radioId)}/metrics/useragent`;
      const headResp = await authFetch(`${base}/summary?${common}`, { method: 'HEAD' });
      if (!headResp.ok) throw new Error(`ua_head_${headResp.status}`);

      const resp = await authFetch(`${base}/summary?${common}`);
      const json = await resp.json();

      if (typeof json.total !== 'number') throw new Error('UA: invalid response');
      setSummary(json);
      setTotal(json.total);
    } catch (e) {
      setError(e?.message || t('listeners.errors.generic_error'));
      setSummary(null);
      setTotal(null);
    }
    setLoading(false);
  }, [isAuth, radioId, startDate, endDate, country, t]);

  const onSubmit = (e) => {
    e.preventDefault();
    fetchSummary();
  };

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        <h2 style={{ marginBottom: 8 }}>Devices & OS</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('listeners.no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      <div
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: 8,
          background: '#f8f8f8',
          color: '#1a1a1a',
          fontSize: 17,
          margin: '8px 0 18px 0',
          boxSizing: 'border-box',
        }}
      >
        {t('user_agent.description')}
      </div>

      <h2 style={{ marginBottom: 8 }}>Devices & OS: {stationName || shortcode}</h2>

      <form style={filtersFormStyle} onSubmit={onSubmit}>
        <table style={formTableStyle}>
          <tbody>
            <tr>
              <td style={formLabelCellStyle}>
                <label htmlFor="ua-start-date" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
                  <span role="img" aria-hidden="true">📅</span> {t('listeners.from_date')}
                </label>
              </td>
              <td style={formControlCellStyle}>
                <div style={dateFieldWrapperStyle}>
                  <input
                    id="ua-start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={controlStyle}
                  />
                </div>
              </td>

              <td style={{ width: 24 }} />

              <td style={formLabelCellStyle}>
                <label htmlFor="ua-end-date" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
                  <span role="img" aria-hidden="true">📅</span> {t('listeners.to_date')}
                </label>
              </td>
              <td style={formControlCellStyle}>
                <div style={dateFieldWrapperStyle}>
                  <input
                    id="ua-end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={controlStyle}
                  />
                </div>
              </td>
            </tr>

            <tr>
              <td style={formLabelCellStyle}>
                <label htmlFor="ua-country" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
                  <span role="img" aria-hidden="true">🌍</span> {t('listeners.country')}
                </label>
              </td>
              <td style={formControlCellStyle}>
                <div style={dateFieldWrapperStyle}>
                  <select
                    id="ua-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    style={controlStyle}
                  >
                    <option value="">{t('listeners.all')}</option>
                    {countries.map(({ name, flag }) => (
                      <option key={name} value={name}>
                        {flag ? `${flag} ${name}` : name}
                      </option>
                    ))}
                  </select>
                </div>
              </td>

              <td style={formSpacerCellStyle} />

              <td colSpan={2} style={buttonCellStyle}>
                <div style={buttonWrapperStyle}>
                  <button type="submit" style={showBtn}>
                    {t('listeners.show')}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {loading && <div>{t('common.loading')}</div>}
      {error && !loading && <div style={{ color: 'red', fontWeight: 500, margin: '18px 0' }}>{error}</div>}

      {!loading && !error && total !== null && (
        <div
          style={{
            textAlign: 'right',
            fontSize: '1.2em',
            fontWeight: 'bold',
            padding: '0.4em',
            border: '1px solid #333',
            marginBottom: '1em',
            marginTop: '1em',
            borderRadius: 7,
            background: '#fafafa',
          }}
        >
          {totalLabel}: {fmtNumber(total ?? 0, locale)}
        </div>
      )}

      {!loading && !error && summary && (
        <>
          <Section title={sectionTitles.deviceType} data={summary.device_type} locale={locale} tableLabels={tableLabels} />
          <Section title={sectionTitles.os} data={summary.os} locale={locale} tableLabels={tableLabels} />
          <Section title={sectionTitles.deviceFamily} data={summary.top_device_family} locale={locale} tableLabels={tableLabels} />
          <Section title={sectionTitles.client} data={summary.top_client} locale={locale} tableLabels={tableLabels} />
          <Section title={sectionTitles.browser} data={summary.top_browser} locale={locale} tableLabels={tableLabels} />
        </>
      )}

      {!loading && !error && !summary && <div>{t('listeners.no_data')}</div>}
    </div>
  );
}

const widgetStyle = {
  maxWidth: 900,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001',
};

const filtersFormStyle = {
  marginBottom: '1em',
  width: '100%',
};

const formTableStyle = {
  width: '60%',
  borderCollapse: 'collapse',
};

const formLabelCellStyle = {
  padding: '6px 4px 6px 0',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

const formControlCellStyle = {
  padding: '6px 0',
  verticalAlign: 'middle',
  width: 'auto',
};

const formSpacerCellStyle = {
  width: 4,
};

const dateFieldWrapperStyle = {
  maxWidth: 195,
  marginRight: 30,
};

const PIE_CHART_BG = '#f1f3ff';
const PIE_COLORS = ['#3867fa', '#34c759', '#ff9500', '#ff375f', '#af52de', '#5ac8fa', '#ffd60a', '#8e8e93'];

const sectionContentStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 32,
  alignItems: 'flex-start',
};

const sectionTableWrapperStyle = {
  flex: '1 1 360px',
  minWidth: 280,
};

const pieBlockStyle = {
  minWidth: 220,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
};

const pieChartStyle = {
  width: 200,
  height: 200,
  borderRadius: '50%',
  position: 'relative',
  boxShadow: '0 4px 14px #0000001a',
};

const pieChartCenterStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 16,
  color: '#1a1a1a',
};

const pieLegendStyle = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const legendRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 14,
};

const legendColorDotStyle = {
  width: 12,
  height: 12,
  borderRadius: '50%',
  display: 'inline-block',
};

const ACTION_BUTTON_WIDTH = 120;
const ACTION_BUTTON_HEIGHT = 44;
const ACTION_BUTTON_GAP = 12;

const buttonCellStyle = {
  padding: '6px 0',
  verticalAlign: 'middle',
  display: 'flex',
  justifyContent: 'flex-start',
};

const buttonWrapperStyle = {
  display: 'inline-flex',
  gap: ACTION_BUTTON_GAP,
  justifyContent: 'flex-start',
};

const fieldLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontWeight: 600,
  color: '#1a1a1a',
  whiteSpace: 'nowrap',
  minWidth: 110,
  width: 'auto',
};

const controlStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #ccc',
  fontSize: 16,
  boxSizing: 'border-box',
};

const showBtn = {
  background: '#3867fa',
  color: '#fff',
  border: 'none',
  padding: '10px 16px',
  borderRadius: 8,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: ACTION_BUTTON_WIDTH,
  minWidth: ACTION_BUTTON_WIDTH,
  height: ACTION_BUTTON_HEIGHT,
  minHeight: ACTION_BUTTON_HEIGHT,
  whiteSpace: 'nowrap',
  transition: 'background 0.2s ease',
};

const thTd = {
  border: '1px solid #ddd',
  padding: 6,
  textAlign: 'left',
  verticalAlign: 'top',
};
